import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SimpleTabs } from "../../components/simplePage/simpleTabs.tsx";
import { DataGrid } from "../../components/dataGrid/dataGrid.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/ui/modal.tsx";
import {
  getCustomerInfo,
  setCustomerData,
  setCustomerPhone,
  customerGdprRemoval,
  type CustomerGdprRemovalArgs,
  CustomerOp,
  type CustomerDetails,
} from "../../api/customer.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import type { InputParam } from "../../types/grid.ts";
import { ParamType } from "../../types/grid.ts";
import { ResendEmailModal } from "../../components/customerEmail/resendEmailModal.tsx";
import { handleEmailLogButton, type ResendArgs } from "../../components/customerEmail/handleEmailLogButton.ts";
import { formatWallClock } from "../../util/serverTime.ts";
import formStyles from "../../styles/formPage.module.scss";

/**
 * Unified Customer editor. Used by:
 *   - `searchEditCustomerPage` grid row click
 *   - `PagePopup` editcustomer
 *   - `resolveHyperlinkAction` LinkID 9 & 24 (flows via PagePopup)
 *   - `/page/editcustomer?edit=<id>` direct route
 *
 * Always renders as `GridEditModal` (readOnly, sub-sections self-save). Full
 * editor: quick-edit panel (info / contact / access / admin) + data tabs
 * (Transactions, Emails, Declined, Notes, Refunded).
 */

function GdprRemovalDialog({ customerId, onClose }: { customerId: number; onClose: () => void }) {
  const { toast } = useToast();
  const [abortOnBlock, setAbortOnBlock] = useState(true);
  const [abortOnRisk, setAbortOnRisk] = useState(true);
  const [abortOnSuspicious, setAbortOnSuspicious] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const args: CustomerGdprRemovalArgs = {
        CustomerID: customerId,
        OverrideBlock: !abortOnBlock,
        OverrideRisk: !abortOnRisk,
        OverrideSuspicious: !abortOnSuspicious,
      };
      const res = await customerGdprRemoval(args);
      if (res.bError) {
        toast(res.sMessage ?? "GDPR removal failed.", "error");
        setResult(res.sMessage ?? "Error");
        return;
      }
      setResult(res.oData ?? "Done");
      toast("GDPR removal completed.", "success");
    } catch (err) {
      setResult(String(err));
      toast(String(err), "error");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title={`GDPR Removal — Customer #${customerId}`} onClose={onClose} />
      <ModalBody>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
            <input type="checkbox" checked={abortOnBlock} onChange={(e) => setAbortOnBlock(e.target.checked)} />
            Abort on account blocked
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
            <input type="checkbox" checked={abortOnRisk} onChange={(e) => setAbortOnRisk(e.target.checked)} />
            Abort on risk analysis fail
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
            <input type="checkbox" checked={abortOnSuspicious} onChange={(e) => setAbortOnSuspicious(e.target.checked)} />
            Abort on suspicious activity
          </label>
        </div>
        {result !== null && (
          <textarea readOnly value={result}
            style={{ width: "100%", minHeight: 80, marginTop: "var(--space-3)", fontSize: "var(--text-sm)", resize: "vertical" }} />
        )}
      </ModalBody>
      <ModalFooter>
        <button className={formStyles.secondaryBtn} onClick={onClose}>Close</button>
        <button className={formStyles.dangerBtn} onClick={handleExecute} disabled={executing}>
          {executing ? "Executing…" : "Execute GDPR Removal"}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function CustomerQuickEditPanel({ userId, isSuperAdmin, onGdprOpen, onSaved }: {
  userId: number;
  isSuperAdmin: boolean;
  onGdprOpen: () => void;
  /** Fires after any successful inline save so the parent popup can mark
   *  itself dirty and request a grid refresh on close. */
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [blockOverride, setBlockOverride] = useState("");
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(0);

  useEffect(() => {
    if (userId <= 0) return;
    const id = ++loadRef.current;
    setLoading(true);
    getCustomerInfo(userId)
      .then((res) => {
        if (id !== loadRef.current) return;
        if (res.bError || !res.oData) {
          toast(res.sMessage ?? "Failed to load customer", "error");
          return;
        }
        setCustomer(res.oData);
        setEmail(res.oData.Email ?? "");
        setPhone(res.oData.Phone ?? "");
        setBlockOverride(res.oData.BlockOverrideDate?.slice?.(0, 10) ?? "");
      })
      .catch((err) => toast(String(err), "error"))
      .finally(() => { if (id === loadRef.current) setLoading(false); });
  }, [userId, toast]);

  const handleSaveEmail = async () => {
    const res = await setCustomerData(userId, CustomerOp.SetEmail, email);
    if (res.bError || !res.oData) { toast(res.sMessage ?? "Failed to save email", "error"); return; }
    toast("Email updated.", "success");
    setCustomer((c) => (c ? { ...c, Email: email } : c));
    onSaved?.();
  };

  const handleSavePhone = async () => {
    const res = await setCustomerPhone(userId, phone);
    if (res.bError || !res.oData) { toast(res.sMessage ?? "Failed to save phone", "error"); return; }
    toast("Phone updated.", "success");
    setCustomer((c) => (c ? { ...c, Phone: phone } : c));
    onSaved?.();
  };

  const handleToggleBlock = async () => {
    if (!customer) return;
    const newBlocked = !customer.Blocked;
    const res = await setCustomerData(userId, CustomerOp.SetBlocked, String(newBlocked));
    if (res.bError || !res.oData) { toast(res.sMessage ?? "Failed to update block state", "error"); return; }
    setCustomer((c) => (c ? { ...c, Blocked: newBlocked } : c));
    toast(newBlocked ? "Customer blocked." : "Customer unblocked.", "success");
    onSaved?.();
  };

  const handleSetBlockOverride = async () => {
    const res = await setCustomerData(userId, CustomerOp.SetBlockOverride, blockOverride || "");
    if (res.bError || !res.oData) { toast(res.sMessage ?? "Failed to save block override", "error"); return; }
    setCustomer((c) => (c ? { ...c, BlockOverrideDate: blockOverride } : c));
    toast(blockOverride ? "Block override set." : "Block override removed.", "success");
    onSaved?.();
  };

  if (loading) return <div>Loading…</div>;
  if (!customer) return <div>Customer not found.</div>;

  const statusBadgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--space-1)",
    padding: "var(--space-0-5) var(--space-2)",
    borderRadius: "var(--radius-full)",
    fontSize: "var(--text-xs)",
    fontWeight: 600,
    letterSpacing: "0.02em",
    background: customer.Blocked ? "var(--color-error-muted, var(--color-primary-muted))" : "var(--color-success-muted, var(--color-bg-muted))",
    color: customer.Blocked ? "var(--color-error)" : "var(--color-success)",
  };
  const inputWithActionStyle: React.CSSProperties = { display: "flex", flex: 1, minWidth: 0, alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" };
  const actionBtnStyle: React.CSSProperties = { whiteSpace: "nowrap", flexShrink: 0 };

  return (
    <div className={formStyles.formContainer}>
      <div className={formStyles.sectionCard}>
        <div className={formStyles.sectionCardTitle}>Customer info</div>
        <div className={formStyles.fieldRow}><label>ID</label><input className={formStyles.fieldInput} value={customer.CustomerID} readOnly /></div>
        <div className={formStyles.fieldRow}><label>Name</label><input className={formStyles.fieldInput} value={customer.Name ?? ""} readOnly /></div>
        <div className={formStyles.fieldRow}><label>Card Expire Date</label><input className={formStyles.fieldInput} value={customer.CardExpireDate ?? ""} readOnly /></div>
        {customer.CardIBAN && <div className={formStyles.fieldRow}><label>Customer IBAN</label><input className={formStyles.fieldInput} value={customer.CardIBAN} readOnly /></div>}
      </div>

      <div className={formStyles.sectionCard}>
        <div className={formStyles.sectionCardTitle}>Contact</div>
        <div className={formStyles.fieldRow}>
          <label>Email</label>
          <div style={inputWithActionStyle}>
            <input className={formStyles.fieldInput} value={email} onChange={(e) => setEmail(e.target.value)} />
            <button className={formStyles.primaryBtn} onClick={handleSaveEmail} disabled={email === (customer.Email ?? "")} style={actionBtnStyle}>Save</button>
          </div>
        </div>
        <div className={formStyles.fieldRow} style={{ marginBottom: 0 }}>
          <label>Phone</label>
          <div style={inputWithActionStyle}>
            <input className={formStyles.fieldInput} value={phone} onChange={(e) => setPhone(e.target.value)} />
            <button className={formStyles.primaryBtn} onClick={handleSavePhone} disabled={phone === (customer.Phone ?? "")} style={actionBtnStyle}>Save</button>
          </div>
        </div>
      </div>

      <div className={formStyles.sectionCard}>
        <div className={formStyles.sectionCardTitle}>Access</div>
        <div className={formStyles.fieldRow}>
          <label>Status</label>
          <div style={inputWithActionStyle}>
            <span style={statusBadgeStyle}>{customer.Blocked ? "● Blocked" : "● Active"}</span>
            <div style={{ flex: 1 }} />
            <button
              className={customer.Blocked ? formStyles.primaryBtn : formStyles.dangerBtn}
              onClick={handleToggleBlock}
              style={actionBtnStyle}
            >
              {customer.Blocked ? "Remove block" : "Manually block"}
            </button>
          </div>
        </div>
        <div className={formStyles.fieldRow} style={{ marginBottom: customer.BlockReason ? "var(--space-2)" : 0 }}>
          <label>Override until</label>
          <div style={inputWithActionStyle}>
            <input type="date" className={formStyles.fieldInput} value={blockOverride} onChange={(e) => setBlockOverride(e.target.value)} />
            <button className={formStyles.secondaryBtn} onClick={handleSetBlockOverride} style={actionBtnStyle}>{blockOverride ? "Set" : "Remove"}</button>
          </div>
        </div>
        {customer.BlockReason && (
          <textarea
            readOnly
            className={formStyles.fieldTextarea}
            style={{ background: "var(--color-bg-muted)", minHeight: 60, marginBottom: 0 }}
            value={`(${formatWallClock(customer.BlockDate) || customer.BlockDate}) Customer was blocked because:\n${customer.BlockReason}`}
          />
        )}
      </div>

      {isSuperAdmin && (
        <div className={formStyles.sectionCard}>
          <div className={formStyles.sectionCardTitle}>Administrative</div>
          <p style={{ margin: "0 0 var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
            Irreversible actions — reserved for super-admins.
          </p>
          <button className={formStyles.dangerBtn} onClick={onGdprOpen}>GDPR Removal</button>
        </div>
      )}
    </div>
  );
}

function CustomerDetailsTabs({ userId }: { userId: number }) {
  const { toast } = useToast();
  const [resendArgs, setResendArgs] = useState<ResendArgs | null>(null);
  const allTabs = useMemo(
    () => [
      { key: "transactions", label: "Transactions", gridName: "transactionStatus", filterColumn: "iUserID", paramType: ParamType.Specified },
      { key: "emails", label: "Emails", gridName: "customerEmailLog", filterColumn: "iUserID" },
      { key: "declined", label: "Declined", gridName: "customerDeclined", filterColumn: "iUserID" },
      { key: "blocked", label: "Blocked", gridName: "customerBlock", filterColumn: "iUserID" },
      { key: "discissues", label: "Disc payment issues", gridName: "discsPaymentProblem", filterColumn: "iUserID" },
      { key: "discsout", label: "Discs out", gridName: "discsOut", filterColumn: "iUserID" },
      { key: "notes", label: "Notes", gridName: "customerAdminNotes", filterColumn: "iCustomerID" },
      { key: "reservations", label: "Reservations", gridName: "customerReservation", filterColumn: "iUserID" },
      { key: "refunded", label: "Refunded", gridName: "refundedTransaction", filterColumn: "iUserID" },
    ],
    []
  );
  const tabs = useMemo(() => allTabs.filter((t) => !["blocked", "discissues", "discsout", "reservations"].includes(t.key)), [allTabs]);

  return (
    <>
      <SimpleTabs
        tabs={tabs.map((tab) => ({
          key: tab.key,
          label: tab.label,
          children: (
            <DataGrid
              key={`${tab.gridName}-${userId}`}
              name={tab.gridName}
              staticFilters={[{ sColumn: tab.filterColumn, oValue: userId, Type: tab.paramType }] as InputParam[]}
              autoSearch
              compact
              onButtonClick={tab.gridName === "customerEmailLog"
                ? (info, row, colIndex) => handleEmailLogButton(info, row, colIndex, toast, setResendArgs)
                : undefined}
            />
          ),
        }))}
      />
      {resendArgs && (
        <ResendEmailModal
          open
          emailLogId={resendArgs.emailLogId}
          transactionId={resendArgs.transactionId}
          customerEmail={resendArgs.customerEmail}
          onClose={() => setResendArgs(null)}
        />
      )}
    </>
  );
}


export function EditCustomerPage({ popupEditId, onClose, onSaved }: { popupEditId?: string; onClose?: () => void; onSaved?: () => void } = {}) {
  const { isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [gdprOpen, setGdprOpen] = useState(false);

  const idRaw = popupEditId ?? searchParams.get("id") ?? searchParams.get("userid") ?? searchParams.get("edit") ?? "0";
  const userId = parseInt(idRaw, 10);
  const closeModal = onClose ?? (() => navigate(-1));

  if (!Number.isFinite(userId) || userId <= 0) {
    return (
      <GridEditModal
        title="Customer details"
        readOnly
        cancelLabel="Close"
        onClose={closeModal}
        onSave={() => { }}
      >
        <div>Missing or invalid user id parameter. Use ?edit=&lt;userId&gt;.</div>
      </GridEditModal>
    );
  }

  return (
    <>
      <GridEditModal
        title={`Customer — #${userId}`}
        wide
        maxWidth="1400px"
        readOnly
        cancelLabel="Close"
        footerLeft={isSuperAdmin ? (
          <button className={formStyles.dangerBtn} onClick={() => setGdprOpen(true)}>GDPR Removal</button>
        ) : undefined}
        onClose={closeModal}
        onSave={() => { }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "var(--space-3)", alignItems: "start", minHeight: 0 }}>
          <div style={{ minWidth: 0 }}>
            <CustomerQuickEditPanel userId={userId} isSuperAdmin={isSuperAdmin} onGdprOpen={() => setGdprOpen(true)} onSaved={onSaved} />
          </div>
          <div style={{ minWidth: 0 }}>
            <CustomerDetailsTabs userId={userId} />
          </div>
        </div>
      </GridEditModal>
      {gdprOpen && <GdprRemovalDialog customerId={userId} onClose={() => setGdprOpen(false)} />}
    </>
  );
}
